
import { navigateToWrite } from '@/utils/navigationUtils';
import {
  Vue,
  Component,
  Prop,
  Watch,
} from 'vue-property-decorator';
import Pagination from '@/components/board/pagination.vue';
import {
  getCompanyInfo,
  getMajorCategory,
  searchCompany,
  addCompanyLog,
} from '@/api/company';
import { ICompanySearch, ICompanyLog } from '@/types/company';
import { UserModule } from '@/store/user';
import { getUserInfo } from '@/api/user';

@Component({
  name: 'CompanyList',
  components: {
    Pagination,
  },
})
export default class extends Vue {
  // 세션 스토리지 키 정의
  private readonly SESSION_KEY = 'STARTUP_LIST_STATE';

  private isInitializing = false; // 초기화 중 Watch 실행 방지 플래그

  beforeRouteLeave(to: any, from: any, next: any) {
    // 이동하려는 페이지(to)가 '상세 페이지'가 아니면 세션을 삭제합니다.
    // 상세페이지의 name이 'FirstupDetail'이라고 가정합니다.
    if (to.name !== 'FirstupDetail') {
      sessionStorage.removeItem(this.SESSION_KEY);
    }
    next();
  }

  // [수정] 상세 페이지 이동 시 현재 상태 저장
  checkAuthAndNavigate(uid: string): void {
    if (!UserModule.token) {
      alert('로그인이 필요한 서비스입니다.');
    } else {
      const state = {
        listQuery: this.listQuery,
        selectedCategoryList: this.selectedCategoryList,
        selectedCategoryTargetList: this.selectedCategoryTargetList,
        selectedRegion: this.selectedRegion,
        allCheck: this.allCheck,
      };
      // 상세 페이지로 떠날 때 저장
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(state));

      this.$router.push({
        name: 'FirstupDetail',
        params: { firstupUid: uid },
      });
    }
  }

  // IR 매칭 버튼 클릭 시 로그인 체크
  private checkAuthAndNavigateToWrite(uid: string): void {
    if (!UserModule.token) {
      alert('로그인이 필요한 서비스입니다.');
    } else if (!this.isIrMatchActive) {
      alert('투자사 또는 대중견기업만 IR 매칭이 가능합니다.');
    } else {
      this.handleNavigateToWrite(uid);
    }
  }

  @Watch('selectedCategoryList')
  private handleChangeCategoryList() {
    // [수정] 초기화 중에는 Watch에 의한 자동 검색을 막음
    if (this.isInitializing) return;

    const totalLength = this.category.children.length;
    const currentLength = this.selectedCategoryList.length;

    if (currentLength === totalLength && totalLength > 0) {
      this.allCheck = true;
    } else {
      this.allCheck = false;
    }

    // 산업분야 카테고리는 클릭 시 즉시 검색
    this.handleSearch();
  }

  private uData: any = [];

  private frCname = '';

  private toCname = '';

  private sort = '';

  private stageRank: { [key: string]: number } = {
    'Pre-A': 1,
    Seed: 2,
    'Series A': 3,
    'Series B': 4,
  };

  private parseNumber(val: any): number {
    if (!val) return 0;
    // 문자열에서 숫자, 마침표, 마이너스 기호만 남기고 제거 (예: "1,000" -> 1000)
    const numStr = String(val).replace(/[^0-9.-]/g, '');
    return Number(numStr) || 0;
  }

  private handleSort(field: string) {
    const currentSort = this.listQuery.sort.split(',');
    const currentField = currentSort[0];
    const currentOrder = currentSort[1] || '';

    let newOrder = 'desc'; // 기본 내림차순 (높은 금액, 최신 단계가 위로)

    if (currentField === field) {
      // 같은 필드 클릭 시 정렬 순서 반전 (desc <-> asc)
      newOrder = currentOrder === 'desc' ? 'asc' : 'desc';
    }

    // 정렬 쿼리 업데이트
    this.listQuery.sort = `${field},${newOrder}`;

    // 1. 즉각적인 UI 반응을 위해 클라이언트 사이드 정렬 먼저 수행
    this.applyClientSideSort();

    // 2. 전체 데이터 기준 정렬을 위해 서버 API 재호출 (Top 5가 바뀔 수 있으므로 필수)
    this.fetchCompanies();
  }

  private applyClientSideSort() {
    if (!this.listQuery.sort) return;

    const [field, order] = this.listQuery.sort.split(',');
    const isAsc = order === 'asc';

    // 정렬 로직 함수
    const sortFunc = (a: any, b: any) => {
      let valA;
      let valB;

      if (field === 'coIrPart') {
        // 투자단계: 랭킹 맵핑 이용
        valA = this.stageRank[a.coIrPart] || 0; // 매칭 안되면 0 (맨 뒤 혹은 맨 앞)
        valB = this.stageRank[b.coIrPart] || 0;
      } else if (field === 'coIrHopeM' || field === 'viewCnt') {
        // 금액, 조회수: 숫자 비교
        valA = this.parseNumber(a[field]);
        valB = this.parseNumber(b[field]);
      } else if (field === 'modifyDate') {
        // DATE: 날짜 문자열 비교 (YYYY-MM-DD 형식으로 가정)
        valA = a[field] || '';
        valB = b[field] || '';
      } else {
        // 그 외: 문자열 비교
        valA = a[field] || '';
        valB = b[field] || '';
      }

      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    };

    // [중요] 화면에 보이는 Top 5 리스트 정렬
    if (this.companyListT5 && this.companyListT5.length > 0) {
      this.companyListT5.sort(sortFunc);
    }

    // [중요] 메인 리스트도 정렬 (데이터가 있다면)
    if (this.companyList && this.companyList.length > 0) {
      this.companyList.sort(sortFunc);
    }
  }

  private fetchCompanies(coBizPart?: string) {
    // 1. 산업분야 값 변환
    let finalBizPart = '';
    if (coBizPart !== undefined) {
      finalBizPart = coBizPart;
    } else {
      const totalCount = this.category.children.length;
      const selectCount = this.selectedCategoryList.length;
      if (selectCount === totalCount || selectCount === 0) {
        finalBizPart = '';
      } else {
        finalBizPart = this.selectedCategoryList.join(',');
      }
    }

    let finalIrPart = '';

    const totalIrCount = this.categoryTarget.length - 1;
    const selectIrCount = this.selectedCategoryTargetList.length;

    if (selectIrCount === totalIrCount || selectIrCount === 0) {
      finalIrPart = '';
    } else {
      // 그 외에는 선택된 값들을 콤마로 연결
      finalIrPart = this.selectedCategoryTargetList.join(',');
    }

    let apiPage = this.listQuery.page;
    if (apiPage < 0) apiPage = 0;

    const searchParams: any = {
      page: apiPage,
      size: this.listQuery.size,
      coDept: 'S',
      coBizPart: finalBizPart,
      coAddr: this.selectedRegion,
      coIrPart: finalIrPart, // 변환된 값 사용
      searchValue: this.listQuery.searchValue,
      sort: this.listQuery.sort || '',
    };

    // Top 5
    const searchParamsT5 = { ...searchParams, size: 5, sort: this.listQuery.sort || '' };

    searchCompany(searchParamsT5).then((res) => {
      this.companyListT5 = res.data.content;
    }).catch((e) => console.error(e));

    searchCompany(searchParams).then((res) => {
      this.companyList = res.data.content;
      this.totalElements = res.data.totalElements;
      this.totalPages = res.data.totalPages;
    }).catch((e) => console.error(e));
  }

  async created() {
    if (UserModule.token) {
      // 2. 사용자 정보 가져오기
      const uRes = await getUserInfo();
      const { uid } = uRes.data;

      if (uid) {
        UserModule.setUserUid(uid);
        const ucRes = await getCompanyInfo(uid);
        this.uData = ucRes.data;
        this.frCname = this.uData.coName || ''; // Modified
        // console.log('사용자 데이터:', this.uData);
        // this.frCname = this.uData.coName || '';
      }
    }
  }

  mounted() {
    this.init();
  }

  // 투자단계 전체 선택 여부
  get allTargetsSelected() {
    const actualTargets = this.categoryTarget.filter((t: any) => t.value !== 'All');
    return this.selectedCategoryTargetList.length === actualTargets.length && actualTargets.length > 0;
  }

  get isIrMatchActive(): boolean {
    return ['I', 'O'].includes(this.uData.coDept);
  }

  // [수정] 각 회사의 파일 목록을 받아서 ord가 '0'인 파일의 UID를 반환하는 함수
  private getMainFileUid(files: any[]): string | null {
    if (!files || files.length === 0) return null;

    // ord가 '0'인 파일 찾기
    const file = files.find((f: any) => String(f.ord) === '0');

    // 찾았으면 그 UID 반환, 없으면(예외처리) 첫 번째 파일이라도 반환할지, 아니면 null 반환할지 결정
    // 여기서는 ord 0이 없으면 null을 반환하여 기본 이미지가 뜨게 함
    return file ? file.fileUid : null;
  }

  private listQuery: any = {
    page: 0,
    size: 6,
    searchValue: '',
    sort: '',
    progress: 0,
    categories: [],
    target: '',
    categories2: '',
    startDate: '',
    endDate: '',
  };

  private companyList: any = [];

  private companyListT5: any = [];

  private selectedCategoryList: any = [];

  private selectedCategoryTargetList: any = [];

  private selectedRegion = '';

  // 사업지역 클릭
  private handleClickRegion(uid: string) {
    this.selectedRegion = uid;
  }

  private category: any = {
    categoryName: '',
    children: [],
  };

  private categoryTarget: any = [
    { label: '전체', value: 'All' },
    { label: 'Seed', value: 'Seed' },
    { label: 'Pre-A', value: 'Pre-A' },
    { label: 'Series A', value: 'Series A' },
    { label: 'Series B', value: 'Series B' },
    { label: 'None', value: 'NONE' },
  ];

  private regionalCategories: any = [
    { categoryName: '수도권', uid: '서울, 경기, 인천' },
    { categoryName: '경남', uid: '경남' },
  ];

  private allCheck = true;

  private totalElements = 0;

  private totalPages = 1;

  get imagePath() {
    return `${process.env.VUE_APP_COMMON_API}/attached-file`;
  }

  // [수정] 디폴트 값을 '전체 선택' 상태로 변경
  private async init() {
    this.isInitializing = true; // 플래그 시작
    const savedState = sessionStorage.getItem(this.SESSION_KEY);

    sessionStorage.removeItem(this.SESSION_KEY);

    try {
      // 1. 먼저 카테고리 목록을 가져옵니다.
      const mres = await getMajorCategory();
      const children = mres.data.map((item: any) => ({
        uid: item.majorCategory,
        categoryName: item.majorCategory,
      }));
      this.category.children = children;

      if (savedState) {
        // 2. 저장된 상태가 있으면 복구
        const parsed = JSON.parse(savedState);
        this.listQuery = parsed.listQuery;
        this.selectedCategoryList = parsed.selectedCategoryList;
        this.selectedCategoryTargetList = parsed.selectedCategoryTargetList;
        this.selectedRegion = parsed.selectedRegion;
        this.allCheck = parsed.allCheck;

        // 복구 시에는 handleSearch(0으로 초기화됨)를 쓰지 않고 현재 페이지로 바로 요청
        this.fetchCompanies();
      } else {
        // 3. 저장된 상태가 없으면 기본값 세팅
        this.selectedCategoryTargetList = [];
        this.selectedCategoryList = this.category.children.map((child: any) => child.uid);
        this.allCheck = true;
        this.listQuery.page = 0;
        this.fetchCompanies();
      }
    } catch (e) {
      console.error(e);
    } finally {
      // 약간의 지연 후 플래그 해제 (Watch 충돌 방지)
      setTimeout(() => { this.isInitializing = false; }, 100);
    }
  }

  // 투자단계 클릭
  private handleClickTarget(action: 'All' | 'NONE', event?: Event) {
    // if (event) event.preventDefault();

    const actualTargetValues = this.categoryTarget
      .filter((t: any) => t.value !== 'All')
      .map((t: any) => t.value);

    if (action === 'All') {
      if (this.allTargetsSelected) {
        this.selectedCategoryTargetList = [];
      } else {
        this.selectedCategoryTargetList = actualTargetValues;
      }
    } else if (action === 'NONE') {
      const idx = this.selectedCategoryTargetList.indexOf('NONE');
      if (idx > -1) {
        this.selectedCategoryTargetList.splice(idx, 1);
      } else {
        this.selectedCategoryTargetList.push('NONE');
      }
    }
  }

  private handleChangeAllCheck(e: any) {
    const isChecked = e.target.checked;

    if (isChecked) {
      this.selectedCategoryList = this.category.children.map((c: any) => c.uid);
    } else {
      this.selectedCategoryList = [];
    }
  }

  // 검색 버튼 클릭 시
  private handleSearch() {
    if (this.isInitializing) return;
    this.listQuery.page = 0;
    this.fetchCompanies();
  }

  // 페이지 변경 시
  private handleChangePage(page: number) {
    this.listQuery.page = page;
    this.fetchCompanies();
  }

  // 초기화 버튼
  private searchReset() {
    sessionStorage.removeItem(this.SESSION_KEY); // 세션 삭제
    this.listQuery.searchValue = '';
    this.selectedCategoryList = this.category.children.map((c: any) => c.uid);
    this.allCheck = true;
    this.selectedRegion = '';
    this.selectedCategoryTargetList = [];
    this.listQuery.sort = '';
    this.listQuery.page = 0;

    const allUids = this.category.children.map((c: any) => c.uid);
    this.selectedCategoryList = allUids;
    this.allCheck = true; // 강제로 활성화

    this.fetchCompanies();
  }

  private getThumnail(inputValue: string) {
    if (inputValue.startsWith('http')) return inputValue;
    return `${this.imagePath}/${inputValue}`;
  }

  private getNumber(index: number) {
    const totalSize = (this.listQuery.page - 1) * this.listQuery.size;
    return this.totalElements - totalSize - index;
  }

  private getShortAddr(addr: string) {
    if (!addr) return '';

    return addr.split(' ').slice(0, 2).join(' ');
  }

  private getShortYoutube(httpLink: string) {
    if (!httpLink) return '';

    return httpLink.split('\n')[0];
  }

  // private handleNavigateToWrite(userUid: string) {
  //   navigateToWrite(this.$router, this.$route, userUid);
  //   // this.sendLog('IR 매칭');
  // }

  private handleNavigateToWrite(companyId: string) {
    // companyId는 company.uid 또는 company.userUid일 수 있으므로,
    // 전체 목록에서 해당 ID를 가진 company 객체를 찾습니다.
    const company = [...this.companyList, ...this.companyListT5].find((c) => c.uid === companyId || c.userUid === companyId);

    if (company) {
      // company 객체를 찾았으면 로그를 전송합니다.
      this.sendLog('IR 매칭', company);
      // navigateToWrite에는 userUid를 전달해야 합니다.
      navigateToWrite(this.$router, this.$route, company.userUid);
    }
  }

  // 로그 sort
  private calculateSort(coData: any) { // Modified to accept coData
    if (coData?.coDept && this.uData?.coDept) {
      this.sort = `${this.uData.coDept}${coData.coDept}`;
      // console.log('최종 sort:', this.sort);
    }
  }

  // 로그 추가 함수
  private async logCompanyAction(data: ICompanyLog) {
    try {
      await addCompanyLog(data);
      // console.log('로그 추가 성공:', data);
    } catch (error) {
      // console.error('로그 추가 실패:', error);
    }
  }

  // 로그 데이터 생성
  private sendLog(part: string, coData: any) {
    // 3. 두 데이터 모두 확보 후 sort 계산
    this.calculateSort(coData);
    const logData: ICompanyLog = {
      part,
      frCname: this.frCname,
      toCname: coData.coName,
      sort: this.sort,
    };
    this.logCompanyAction(logData);
  }
}
